import { NumericFormat } from 'react-number-format'
import CustomDatePicker from 'components/datePicker'
import { toJalaliInputDate } from 'utilities/helper/jalaliInputDate'
import { faFull, faPlain, toman } from 'utilities/helper/format'

const QUICK_COUNTS = [6, 12, 18, 24]

export interface ITerms {
    installments: string
    feeType: 'percentage' | 'fixed'
    fee: string
    feeMode: string
    startDate: string
}

/**
 * What the numbers on this step add up to.
 *
 * Exported so the wizard can pin it inside the sticky bar: it is the only
 * figure on the screen the borrower will actually feel, and it changes with
 * every control here, so it has to stay visible while they are changed.
 */
export function termsSummary(amount: number, terms: ITerms) {
    const count = Number(terms.installments || 0)
    const feeValue = Number(terms.fee || 0)
    const feeAmount =
        terms.feeType === 'percentage'
            ? Math.round((amount * feeValue) / 100)
            : feeValue
    // «افزودن به هر قسط» grows what is repaid; «کسر از مبلغ وام» shrinks what
    // is handed over and leaves the repayment equal to the loan
    const totalRepayment =
        terms.feeMode === 'lip_pr' ? amount : amount + feeAmount
    return {
        count,
        feeAmount,
        totalRepayment,
        perInstallment: count > 0 ? Math.ceil(totalRepayment / count) : 0,
        handedOver: terms.feeMode === 'lip_pr' ? amount - feeAmount : amount,
    }
}

export function TermsSummary({
    amount,
    terms,
}: {
    amount: number
    terms: ITerms
}) {
    const { count, feeAmount, totalRepayment, perInstallment, handedOver } =
        termsSummary(amount, terms)
    if (count <= 0 || amount <= 0) return null
    return (
        <div className=" rounded-xl bg-brand-soft px-3 py-2">
            <div className=" flex items-baseline justify-between">
                <span className=" text-[11px] text-brand-deep">
                    هر قسط حدود
                </span>
                <span className=" text-sm font-bold tabular-nums text-brand-deep">
                    {toman(perInstallment)}
                </span>
            </div>
            <p className=" mt-0.5 text-[10px] leading-4 text-brand-deep">
                {faPlain.format(count)} قسط، مجموع بازپرداخت{' '}
                {faFull.format(totalRepayment)} تومان
                {feeAmount > 0 && (
                    <>
                        {' '}
                        — کارمزد {faFull.format(feeAmount)} تومان، به دست عضو
                        می‌رسد {faFull.format(handedOver)} تومان
                    </>
                )}
            </p>
        </div>
    )
}

/**
 * Step 4 — «چطور پس بدهد؟».
 *
 * Everything on this screen changes one number the manager actually cares
 * about — the size of each instalment — so that number is computed live at
 * the bottom rather than left to be discovered on the next page. The old form
 * asked for instalment count, fee, fee mode and start date as four unrelated
 * rows and only revealed the schedule after submitting.
 *
 * «نحوهٔ دریافت کارمزد» only appears once a fee is entered: it is meaningless
 * at zero, and قرض‌الحسنه funds mostly charge nothing.
 */
export default function StepTerms({
    amount,
    terms,
    onChange,
}: {
    amount: number
    terms: ITerms
    onChange: (next: ITerms) => void
}) {
    const set = <K extends keyof ITerms>(key: K, value: ITerms[K]) =>
        onChange({ ...terms, [key]: value })

    const count = Number(terms.installments || 0)
    const feeValue = Number(terms.fee || 0)

    return (
        <>
            <div className=" rounded-2xl border border-stroke bg-white p-4">
                <label
                    htmlFor="installments-count"
                    className=" text-xs text-text-secondary"
                >
                    تعداد اقساط
                </label>
                <input
                    id="installments-count"
                    type="text"
                    inputMode="numeric"
                    min={1}
                    value={terms.installments}
                    onChange={(event) =>
                        set('installments', event.target.value)
                    }
                    placeholder="۱۲"
                    className=" mt-1 w-full border-b border-stroke bg-transparent py-2 text-lg font-bold tabular-nums text-heading outline-none placeholder:text-text-muted"
                />
                <div className=" mt-2 flex gap-2">
                    {QUICK_COUNTS.map((value) => (
                        <button
                            key={value}
                            type="button"
                            onClick={() => set('installments', String(value))}
                            className={` flex-1 rounded-full border py-1.5 text-[11px] font-bold transition-colors ${
                                count === value
                                    ? ' border-brand bg-brand-soft text-brand-deep'
                                    : ' border-stroke text-text-secondary'
                            }`}
                        >
                            {faPlain.format(value)}
                        </button>
                    ))}
                </div>
            </div>

            <div className=" mt-3 rounded-2xl border border-stroke bg-white p-4">
                <p className=" text-xs text-text-secondary">کارمزد (اختیاری)</p>
                <div className=" mt-2 flex gap-2">
                    <button
                        type="button"
                        onClick={() => set('feeType', 'percentage')}
                        className={` flex-1 rounded-full border py-1.5 text-[11px] font-bold ${
                            terms.feeType === 'percentage'
                                ? ' border-brand bg-brand-soft text-brand-deep'
                                : ' border-stroke text-text-secondary'
                        }`}
                    >
                        درصدی
                    </button>
                    <button
                        type="button"
                        onClick={() => set('feeType', 'fixed')}
                        className={` flex-1 rounded-full border py-1.5 text-[11px] font-bold ${
                            terms.feeType === 'fixed'
                                ? ' border-brand bg-brand-soft text-brand-deep'
                                : ' border-stroke text-text-secondary'
                        }`}
                    >
                        مبلغ ثابت
                    </button>
                </div>
                <NumericFormat
                    thousandSeparator={terms.feeType === 'fixed'}
                    allowNegative={false}
                    inputMode="numeric"
                    value={terms.fee}
                    onValueChange={(values) => set('fee', values.value)}
                    placeholder={
                        terms.feeType === 'percentage' ? '۰ درصد' : '۰ تومان'
                    }
                    aria-label="میزان کارمزد"
                    className=" mt-2 w-full border-b border-stroke bg-transparent py-2 text-sm font-bold tabular-nums text-heading outline-none placeholder:text-text-muted"
                />
                {terms.feeType === 'percentage' && feeValue > 4 && (
                    <p className=" mt-2 text-[11px] text-danger-on">
                        کارمزد نباید بیش از ۴ درصد مبلغ وام باشد.
                    </p>
                )}

                {feeValue > 0 && (
                    <div className=" mt-3">
                        <p className=" text-[11px] text-text-secondary">
                            نحوهٔ دریافت کارمزد
                        </p>
                        <div className=" mt-1.5 space-y-1.5">
                            {[
                                {
                                    value: 'lip_ma',
                                    label: 'افزودن به هر قسط',
                                    hint: 'عضو کل مبلغ وام را می‌گیرد و کارمزد روی اقساط تقسیم می‌شود.',
                                },
                                {
                                    value: 'lip_pr',
                                    label: 'کسر از مبلغ وام',
                                    hint: 'کارمزد همان اول کم می‌شود و اقساط برابر مبلغ وام‌اند.',
                                },
                            ].map((option) => (
                                <label
                                    key={option.value}
                                    className=" flex cursor-pointer items-start gap-2"
                                >
                                    <input
                                        type="radio"
                                        name="fee-mode"
                                        checked={terms.feeMode === option.value}
                                        onChange={() =>
                                            set('feeMode', option.value)
                                        }
                                        className=" mt-0.5 h-4 w-4 shrink-0 accent-[#74B743]"
                                    />
                                    <span>
                                        <span className=" text-xs text-text-body">
                                            {option.label}
                                        </span>
                                        <span className=" block text-[11px] leading-5 text-text-secondary">
                                            {option.hint}
                                        </span>
                                    </span>
                                </label>
                            ))}
                        </div>
                    </div>
                )}
            </div>

            <div className=" mt-3 rounded-2xl border border-stroke bg-white p-4">
                <p className=" mb-1 text-xs text-text-secondary">
                    تاریخ شروع اقساط
                </p>
                <div dir="rtl">
                    <CustomDatePicker
                        value={toJalaliInputDate(terms.startDate)}
                        setValue={(value: string) => set('startDate', value)}
                        name="startDate"
                        title=""
                        placeHolder="یک ماه بعد"
                    />
                </div>
            </div>
        </>
    )
}
